import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ClickAwayListener,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import { ChevronDown } from 'lucide-react-new';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GetSubAccountFinancialsReport from 'src/api/GetSubAccountFinancialsReport';
import GetSubAccountWalletsReport from 'src/api/GetSubAccountWalletsReport';
import useAuth from 'src/hooks/useAuth';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  totals: {
    fontWeight: 600,
    color: 'var(--color-text)'
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--neutral-8)',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-corner': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'var(--neutral-5)'
    }
  },
  gradient: {
    position: 'absolute',
    width: '99.5%',
    height: '2rem',
    bottom: 0,
    left: 0,
    background: 'linear-gradient(0deg, rgba(23,25,24,1) 0%, rgba(23,25,24,0) 100%)',
    zIndex: 5
  }
}));

const AuditReportSummary = ({ startDate, endDate, subAccounts, saveSummaryStatsExport, saveSummaryWalletsExport }) => {
  const { getAccessToken } = useAuth();
  const theme = useTheme();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [financialStats, setFinancialStats] = useState([]);
  const [financialTotals, setFinancialTotals] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('subAccountName');
  const [selectedDateRange, setSelectedDateRange] = useState(`(${dayjs(startDate).format('MMMM DD, YYYY')} - ${dayjs(endDate).format('MMMM DD, YYYY')})`)
  const [showSummaryToolTip, setShowSummaryToolTip] = useState(false);
  const [showWalletsToolTip, setShowWalletsToolTip] = useState(false);
  const [showFeeToolTip, setShowFeeToolTip] = useState(false);
  var sortedFinancialStats = applySorting(
    financialStats,
    getComparator(order, orderBy)
  );

  const handleColumnSort = tableColumn => {
    const isAsc = orderBy === tableColumn && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(tableColumn);
  };

  const fetchData = useCallback(async () => {
    if (!!subAccounts?.length) {
      const { tokenAvailable, accessToken } = await getAccessToken();
      if (!tokenAvailable) {
        console.error(accessToken);
        return;
      }

      setLoading(true);
      setSelectedDateRange(`(${dayjs(startDate).format('MMMM DD, YYYY')} - ${dayjs(endDate).format('MMMM DD, YYYY')})`)
      Promise.all([
        GetSubAccountFinancialsReport(
          startDate,
          endDate,
          subAccounts,
          'byTimeRange',
          accessToken
        ),
        GetSubAccountWalletsReport(subAccounts, accessToken)
      ])
        .then(([financials, wallets]) => {
          const walletsDownloaded = !!wallets.success && !!wallets.data?.length;
          const financialsDownloaded = !!financials.success &&
            !!financials.data?.length &&
            !!financials.totals?.length;

          if (!!walletsDownloaded) {
            const walletAddresses = wallets.data?.map(wallet => wallet.address);
            const walletsExport = wallets.data?.map(wallet => {
              return {
                'Wallets': wallet.address
              }
            });
            saveSummaryWalletsExport(walletsExport)
            setWallets(walletAddresses);
          }

          if (!!financialsDownloaded) {
            const financialStats = financials.data.map(d => {
                return {
                    ...d,
                    earnedValue: +d.earned,
                    withdrawnValue: +d.withdrawn,
                    balanceValue: +d.balance
                }
            })
            setFinancialStats([...financialStats]);
            setFinancialTotals(financials.totals[0]);
            const csvExport = financials.data.map((stat) => {
              return {
                  'Group': stat.groupName,
                  'Sub-Account': stat.subAccountName,
                  [`ZEC earned ${selectedDateRange}`]: stat.earned,
                  [`ZEC paid ${selectedDateRange}`]: stat.withdrawn,
                  [`ZEC earned during period but not yet paid out`]: stat.balance,
                }
            })
              csvExport.unshift({
              'Group': '',
              'Sub-Account': 'Pool Fee (ZEC)',
              [`ZEC earned ${selectedDateRange}`]: financials.totals[0]?.poolFeeAmount,
              [`ZEC paid ${selectedDateRange}`]:'',
              [`ZEC earned during period but not yet paid out`]: ''
            })
            csvExport.unshift({
              'Group': '',
              'Sub-Account': 'Total',
              [`ZEC earned ${selectedDateRange}`]: financials.totals[0]?.earned,
              [`ZEC paid ${selectedDateRange}`]: financials.totals[0]?.withdrawn,
              [`ZEC earned during period but not yet paid out`]: financials.totals[0]?.balance,
            })
            saveSummaryStatsExport(csvExport)
         }

          setLoading(false);
        })
        .catch(error => {
          console.error('Error: ', error.message);
          const errorMsg = !!error?.message ? error?.message : error;
          setError(errorMsg);
          setLoading(false);
        });
    } else  {
      setFinancialStats([]);
      setWallets([]);
      setLoading(false);
    }
  }, [startDate, endDate, subAccounts.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Accordion
      style={{
        backgroundColor: 'var(--neutral-2)',
        marginBottom: '1.5rem',
        borderRadius: 8
      }}
    >
      <AccordionSummary expandIcon={<ChevronDown size={20} />}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography style={{ fontSize: '1.125rem', fontWeight: 500 }}>
            Hashrate and Rewards Summary
          </Typography>
          <Typography variant="subtitle2" style={{ color: '#A3ACA9' }}>
            Expand to view a summary of the report.
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ padding: '1rem 0.5rem 1rem 1rem' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 'var(--space-s)',
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 500 }} mb={'12px'}>
            Sub-Account Summary
          </Typography>
          <ClickAwayListener onClickAway={() => setShowSummaryToolTip(false)}>
            <Tooltip
              title={
                <p className="m-text" style={{ padding: '10px 0 10px 20px'}}>
                  The summary table's values are based on aggregated data from the selected sub-accounts during the selected date range.
                </p>
              }
              open={showSummaryToolTip}
              arrow
              placement="right"
            >
              <Info
                size={18}
                className="m-text"
                onClick={() => setShowSummaryToolTip(value => !value)}
                style={{ cursor: 'pointer', color: "white", marginTop: 1 }}
              />
            </Tooltip>
          </ClickAwayListener>
        </div>
        <Box sx={{ position: 'relative', marginBottom: '1rem'}}>
         <TableContainer style={{ maxHeight: 250 }} className={classes.scrollbar}>
          <Table style={{ width: '99.5%' }}>
            <TableHead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: 'var(--neutral-2)',
              }}
            >
              <TableRow>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === 'groupName'}
                    direction={orderBy === 'groupName' ? order : 'asc'}
                    onClick={() => handleColumnSort('groupName')}
                    style={{ color: '#A3ACA9'}}
                  >
                    {t('group')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === 'subAccountName'}
                    direction={orderBy === 'subAccountName' ? order : 'asc'}
                    onClick={() => handleColumnSort('subAccountName')}
                    style={{ color: '#A3ACA9'}}
                  >
                    {t('subAccount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === 'earnedValue'}
                    direction={orderBy === 'earnedValue' ? order : 'asc'}
                    onClick={() => handleColumnSort('earnedValue')}
                    style={{ color: '#A3ACA9'}}
                  >
                    {'ZEC earned ' + selectedDateRange}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === 'withdrawnValue'}
                    direction={orderBy === 'withdrawnValue' ? order : 'asc'}
                    onClick={() => handleColumnSort('withdrawnValue')}
                    style={{ color: '#A3ACA9'}}
                  >
                    {'ZEC paid ' + selectedDateRange}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                  align="left"
                >
                  <TableSortLabel
                    active={orderBy === 'balanceValue'}
                    direction={orderBy === 'balanceValue' ? order : 'asc'}
                    onClick={() => handleColumnSort('balanceValue')}
                    style={{ color: '#A3ACA9'}}
                  >
                    ZEC earned during period but not yet paid out
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !!sortedFinancialStats?.length ? sortedFinancialStats.map((stat, index) => {
                return (
                  <TableRow key={'finstat' + index}>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, whiteSpace: 'nowrap' }}>
                      {stat.groupName}
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8 }}>
                      {stat.subAccountName}
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8 }}>
                      {stat.earned}
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8 }}>
                      {stat.withdrawn}
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8 }}>
                      {stat.balance}
                    </TableCell>
                  </TableRow>
                );
              }) : !loading && !sortedFinancialStats.length ?
                <TableRow style={{ height: 100}}>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8 }} colSpan={5} align='center'>
                      <Typography variant='subtitle2'>No Sub-Account data yet</Typography>
                    </TableCell>
                </TableRow>
              : <>
                {
                  [1,2,3,4].map(i => {
                    return <TableRow>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, border: 'none '}}>
                      <Skeleton variant="rounded" height={20} />
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, border: 'none '}}>
                      <Skeleton variant="rounded" height={20} />
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, border: 'none '}}>
                      <Skeleton variant="rounded" height={20} />
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, border: 'none '}}>
                      <Skeleton variant="rounded" height={20} />
                    </TableCell>
                    <TableCell style={{ paddingTop: 8, paddingBottom: 8, border: 'none '}}>
                      <Skeleton variant="rounded" height={20} />
                    </TableCell>
                </TableRow>
                  })
                }
              </>}
            </TableBody>
            <TableFooter
              style={{
                position: 'sticky',
                bottom: '-1px',
                backgroundColor: 'var(--neutral-2)',
                borderBottom: 'none !important'
              }}
            >
              <TableRow>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8, border: 'none' }}
                >
                  <Typography variant="subtitle2" className={classes.totals}>
                    Total
                  </Typography>
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8, border: 'none' }}
                ></TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8, border: 'none' }}
                >
                  {!loading ? <Typography variant="subtitle2" className={classes.totals}>
                    {financialTotals?.earned}
                  </Typography> :
                    <Skeleton variant="rounded" height={20} width={175} />}
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8, border: 'none' }}
                >
                  {!loading ? <Typography variant="subtitle2" className={classes.totals}>
                    {financialTotals?.withdrawn}
                  </Typography> :
                  <Skeleton variant="rounded" height={20}  width={175} />
                  }
                </TableCell>
                <TableCell
                  style={{ paddingTop: 8, paddingBottom: 8, border: 'none' }}
                >
                  {!loading ? <Typography variant="subtitle2" className={classes.totals}>
                    {financialTotals?.balance}
                  </Typography> :
                  <Skeleton variant="rounded" height={20} width={175} />}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        </Box>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 'var(--space-s)',
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 500 }} mb={'12px'}>
            Wallets
          </Typography>
          <ClickAwayListener onClickAway={() => setShowWalletsToolTip(false)}>
            <Tooltip
              title={
                <p className="m-text" style={{ padding: '10px 0 10px 20px'}}>
                  This list contains the addresses of all the selected sub-accounts' ZEC wallets.
                </p>
              }
              open={showWalletsToolTip}
              arrow
              placement="right"
            >
              <Info
                size={18}
                className="m-text"
                onClick={() => setShowWalletsToolTip(value => !value)}
                style={{ cursor: 'pointer', color: "white", marginTop: 1 }}
              />
            </Tooltip>
          </ClickAwayListener>
        </div>
        <Box sx={{ position: 'relative'}}>
            <div
                style={{
                    height: !!loading || !loading && !!wallets.length? 175 : 100,
                    overflowY: 'auto',
                    padding: '8px 0px',
                    marginBottom: '1rem',
                }}
                className={classes.scrollbar}
            >
                { !loading && !!wallets?.length ? wallets.map((wallet, index) => {
                    return (
                    <div
                        key={'wallet' + index}
                        style={{
                          backgroundColor: 'var(--color-background)',
                          marginBottom: 8,
                          padding: '0.75rem 0.5rem',
                          borderRadius: 4,
                          marginRight: 8
                        }}
                    >
                        <Typography variant="subtitle2" style={{ color: '#A3ACA9' }}>
                        { wallet }
                        </Typography>
                    </div>
                    );
                }) : !loading && !wallets.length ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100}}
                >
                  <Typography variant='subtitle2'>No Wallets found</Typography>
                </div> :
                  <div
                    style={{
                      padding: '0 0.5rem',
                      marginRight: 8
                    }}
                  >
                    <Skeleton height={50} />
                    <Skeleton height={50} />
                    <Skeleton height={50} />
                  </div>
                }
            </div>
            <div className={classes.gradient}></div>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <Typography variant="subtitle2" style={{ fontWeight: 500, display: 'flex', alignItems: "center", gap: 5 }}>
          Pool Fee (ZEC) <span>
          <ClickAwayListener onClickAway={() => setShowFeeToolTip(false)}>
            <Tooltip
              title={
                <p className="m-text" style={{ padding: '10px 5px 10px 5px'}}>
                  Fee amount earned by selected sub-accounts, during the selected date range.
                </p>
              }
              open={showFeeToolTip}
              arrow
              placement="bottom"
            >
              <Info
                size={18}
                className="m-text"
                onClick={() => setShowFeeToolTip(value => !value)}
                style={{ cursor: 'pointer', color: "white", marginBottom: -4 }}
              />
            </Tooltip>
          </ClickAwayListener>

          </span> :
          {!loading ? <span style={{ marginLeft: 5 }}>{financialTotals?.poolFeeAmount}</span>
          : <Skeleton variant="rounded" height={20}  width={150} style={{ display: 'inline-block', marginLeft: 5}}/>}
        </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AuditReportSummary;
